/**
 * BankFeedOverviewTab - extracted from BankFeedCenterPage Phase 2.
 * Renders feed-sources card, AI-powered processing info, AI auto-processing
 * settings, and a recent-activity feed (top 5 processed items).
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Brain, CheckCircle2, Activity } from 'lucide-react';
import { FeedSourcesCard } from './FeedSourcesCard';
import { AISettingsCard } from './AISettingsCard';

export const BankFeedOverviewTab = ({
  fileInputRef,
  onOpenUploadStarter,
  onOpenBankingClick,
  connections,
  business,
  aiSettings,
  onToggleAutoApprove,
  savingAiSettings,
  processedItems = [],
  isNewTransaction,
  formatAmount,
  getDirectionIcon,
  getStatusBadge,
}) => {
  return (
    <>
      <FeedSourcesCard
        onFileUploadClick={onOpenUploadStarter || (() => fileInputRef?.current?.click())}
        onOpenBankingClick={onOpenBankingClick}
        connections={connections}
        businessCountry={business?.country}
      />

      <Card className="bg-gradient-to-r from-[#108981]/5 to-[#5FC6C5]/5 border-[#108981]/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#108981]/10 flex items-center justify-center shrink-0">
              <Brain className="w-6 h-6 text-[#108981]" />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">AI-Powered Processing</h3>
              <p className="text-muted-foreground mb-4">
                Every transaction is processed through the AI Accounting Brain which:
              </p>
              <ul className="space-y-2 text-sm">
                {[
                  'Automatically matches payments to invoices and bills',
                  'Creates new documents when no match is found',
                  'Handles partial payments and WHT deductions',
                  'Detects duplicates to prevent double entries',
                  'Creates correct GL entries with full audit trail',
                ].map((line) => (
                  <li key={line} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <AISettingsCard
        autoApproveEnabled={aiSettings.auto_post_medium_confidence}
        onAutoApproveChange={onToggleAutoApprove}
        isLoading={savingAiSettings}
      />

      {processedItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#108981]" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {processedItems.slice(0, 5).map((item, index) => (
                <motion.div
                  key={item.id || index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {getDirectionIcon?.(item.direction)}
                    <div>
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-sm line-clamp-1">{item.description || 'Transaction'}</p>
                        {isNewTransaction?.(item) && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white leading-none"
                            data-testid={`new-badge-${item.id}`}
                          >
                            NEW
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{item.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-semibold ${item.direction === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                      {item.direction === 'inflow' ? '+' : '-'}{formatAmount(item.amount)}
                    </span>
                    {getStatusBadge?.(item.status)}
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default BankFeedOverviewTab;
